import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { CreditCard, Wallet, Smartphone, Truck, ArrowLeft, Shield, Check, Tag } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Payment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const formData = location.state;
  const [paymentMethod, setPaymentMethod] = useState('');
  const [processing, setProcessing] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  if (!formData) {
    navigate('/');
    return null;
  }

  // Fetch payment settings on mount
  useEffect(() => {
    const fetchPaymentSettings = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/payment/settings`);
        setPaymentSettings(response.data);
        
        // Set default payment method to first enabled gateway
        // API returns: razorpay_enabled, paytm_enabled, instamojo_enabled, cod_enabled
        const enabledGateways = ['razorpay', 'paytm', 'instamojo', 'cod'].filter(
          gateway => response.data[`${gateway}_enabled`]
        );
        if (enabledGateways.length > 0) {
          setPaymentMethod(enabledGateways[0]);
        }
      } catch (error) {
        console.error('Failed to fetch payment settings:', error);
        toast({
          title: "Error",
          description: "Failed to load payment options",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchPaymentSettings();
  }, []);

  const handleApplyCoupon = () => {
    // Demo coupons: SAVE50, NEW100, STUDENT20
    const coupons = {
      'SAVE50': { discount: 50, type: 'flat' },
      'NEW100': { discount: 100, type: 'flat' },
      'STUDENT20': { discount: 20, type: 'percentage' }
    };

    const coupon = coupons[couponCode.toUpperCase()];
    if (coupon) {
      setCouponApplied(coupon);
      toast({ 
        title: "Coupon Applied!", 
        description: `You saved ₹${coupon.type === 'flat' ? coupon.discount : Math.round(299 * coupon.discount / 100)}` 
      });
    } else {
      setCouponApplied(null);
      toast({ title: "Invalid Coupon", description: "Please enter a valid coupon code", variant: "destructive" });
    }
  };

  const calculateFinalPrice = () => {
    let price = 299;
    if (couponApplied) {
      if (couponApplied.type === 'flat') {
        price -= couponApplied.discount;
      } else {
        price -= Math.round(price * couponApplied.discount / 100);
      }
    }
    return Math.max(price, 0);
  };

  const handlePayment = async () => {
    // Validate payment method is selected
    if (!paymentMethod) {
      toast({
        title: "Payment Failed",
        description: "Please select a payment method",
        variant: "destructive"
      });
      return;
    }

    setProcessing(true);

    try {
      const userId = localStorage.getItem('userId');
      const finalPrice = calculateFinalPrice();
      
      // Handle different payment methods
      if (paymentMethod === 'cod') {
        // COD: Process immediately with pending status
        if (userId) {
          try {
            await axios.post(`${BACKEND_URL}/api/payment/complete`, {
              user_id: userId,
              amount: finalPrice,
              referral_code: formData.referralCode || null,
              payment_method: 'cod',
              payment_status: 'pending'
            });
          } catch (error) {
            console.error('Failed to process payment:', error);
          }
        }
        
        setProcessing(false);
        toast({ title: "Order Placed", description: "Pay on delivery. Redirecting to results..." });
        
        // Navigate to results with payment confirmed
        setTimeout(() => {
          navigate('/results', { state: { ...formData, paymentConfirmed: true } });
        }, 1000);
        
      } else if (paymentMethod === 'razorpay') {
        // Razorpay: Open payment gateway
        if (!paymentSettings.razorpay_key) {
          setProcessing(false);
          toast({ 
            title: "Payment Gateway Not Configured", 
            description: "Razorpay is not set up. Please use another payment method or contact admin.", 
            variant: "destructive" 
          });
          return;
        }
        
        // In production, you would initialize Razorpay here
        // For now, show info message
        setProcessing(false);
        toast({ 
          title: "Razorpay Integration Required", 
          description: "Admin needs to configure Razorpay API keys in Payment Settings. Using COD for now.", 
          variant: "destructive" 
        });
        
      } else if (paymentMethod === 'paytm') {
        // Paytm: Similar to Razorpay
        if (!paymentSettings.paytm_key) {
          setProcessing(false);
          toast({ 
            title: "Payment Gateway Not Configured", 
            description: "Paytm is not set up. Please use another payment method or contact admin.", 
            variant: "destructive" 
          });
          return;
        }
        
        setProcessing(false);
        toast({ 
          title: "Paytm Integration Required", 
          description: "Admin needs to configure Paytm API keys in Payment Settings. Using COD for now.", 
          variant: "destructive" 
        });
        
      } else if (paymentMethod === 'instamojo') {
        // Instamojo: Redirect to payment link
        if (!paymentSettings.instamojo_payment_link) {
          setProcessing(false);
          toast({ 
            title: "Payment Gateway Not Configured", 
            description: "Instamojo link is not set up. Please use another payment method or contact admin.", 
            variant: "destructive" 
          });
          return;
        }
        
        // Record payment attempt if user is logged in
        if (userId) {
          try {
            await axios.post(`${BACKEND_URL}/api/payment/complete`, {
              user_id: userId,
              amount: finalPrice,
              referral_code: formData.referralCode || null,
              payment_method: 'instamojo',
              payment_status: 'pending'
            });
          } catch (error) {
            console.error('Failed to record payment:', error);
          }
        }
        
        // Open Instamojo payment link in new tab
        const paymentWindow = window.open(paymentSettings.instamojo_payment_link, '_blank');
        
        if (!paymentWindow) {
          setProcessing(false);
          toast({ 
            title: "Pop-up Blocked", 
            description: "Please allow pop-ups for this site and try again.", 
            variant: "destructive" 
          });
          return;
        }
        
        setProcessing(false);
        toast({ 
          title: "Payment Window Opened", 
          description: "Complete payment on Instamojo. After payment, click below to continue.", 
        });
        
        // For now, assume payment is successful when user clicks continue
        // In production, you would verify payment status from Instamojo webhook
        setTimeout(() => {
          navigate('/results', { state: { ...formData, paymentConfirmed: true } });
        }, 2000);
        
      } else {
        // Default case - use COD
        if (userId) {
          try {
            await axios.post(`${BACKEND_URL}/api/payment/complete`, {
              user_id: userId,
              amount: finalPrice,
              referral_code: formData.referralCode || null,
              payment_method: 'cod',
              payment_status: 'pending'
            });
          } catch (error) {
            console.error('Failed to process payment:', error);
          }
        }
        
        setProcessing(false);
        toast({ title: "Order Placed", description: "Proceeding with COD. Redirecting..." });
        
        setTimeout(() => {
          navigate('/results', { state: { ...formData, paymentConfirmed: true } });
        }, 1000);
      }
      
    } catch (error) {
      console.error('Payment error:', error);
      setProcessing(false);
      toast({ 
        title: "Payment Failed", 
        description: error.message || "Something went wrong. Please try again.", 
        variant: "destructive" 
      });
    }
  };

  const allPaymentOptions = [
    {
      id: 'razorpay',
      name: 'Razorpay',
      description: 'Credit/Debit Card, UPI, Net Banking',
      icon: CreditCard,
      popular: true
    },
    {
      id: 'paytm',
      name: 'Paytm',
      description: 'Paytm Wallet, UPI',
      icon: Wallet,
      popular: false
    },
    {
      id: 'instamojo',
      name: 'Instamojo',
      description: 'All payment methods',
      icon: Smartphone,
      popular: false
    },
    {
      id: 'cod',
      name: 'Cash on Delivery',
      description: 'Pay when you receive physical report',
      icon: Truck,
      popular: false
    }
  ];

  // Filter payment options based on admin settings
  // API format: razorpay_enabled, paytm_enabled, instamojo_enabled, cod_enabled
  const paymentOptions = paymentSettings 
    ? allPaymentOptions.filter(option => paymentSettings[`${option.id}_enabled`])
    : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4">
          <Button variant="ghost" onClick={() => navigate('/')} className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Complete Your Payment</h1>
            <p className="text-slate-600">Secure payment to access your personalized prediction report</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Payment Methods */}
            <div className="md:col-span-2">
              <Card className="shadow-lg border-slate-200">
                <CardHeader>
                  <CardTitle>Select Payment Method</CardTitle>
                  <CardDescription>Choose your preferred payment option</CardDescription>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                  ) : paymentOptions.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-slate-600 mb-4">No payment methods available at the moment.</p>
                      <p className="text-sm text-slate-500">Please contact support for assistance.</p>
                    </div>
                  ) : (
                    <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-4">
                      {paymentOptions.map((option) => {
                      const Icon = option.icon;
                      return (
                        <div
                          key={option.id}
                          className={`flex items-center space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                            paymentMethod === option.id
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                          onClick={() => setPaymentMethod(option.id)}
                        >
                          <RadioGroupItem value={option.id} id={option.id} />
                          <div className="flex items-center gap-3 flex-1">
                            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                              <Icon className="w-5 h-5 text-slate-700" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <Label htmlFor={option.id} className="text-base font-medium cursor-pointer">
                                  {option.name}
                                </Label>
                                {option.popular && (
                                  <Badge className="bg-green-100 text-green-800 text-xs">Popular</Badge>
                                )}
                              </div>
                              <p className="text-sm text-slate-600">{option.description}</p>
                            </div>
                          </div>
                        </div>
                      );
                      })}
                    </RadioGroup>
                  )}

                  {/* Discount Coupon Section */}
                  {!loading && paymentOptions.length > 0 && (
                  <>
                  <div className="mt-6 pt-6 border-t space-y-2">
                    <Label htmlFor="couponCode" className="text-sm font-medium">Have a Discount Coupon?</Label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Tag className="absolute left-3 top-3 w-4 h-4 text-slate-500" />
                        <Input
                          id="couponCode"
                          type="text"
                          placeholder="Enter coupon code"
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                          className="pl-10"
                          disabled={couponApplied}
                        />
                      </div>
                      {!couponApplied ? (
                        <Button type="button" onClick={handleApplyCoupon} variant="outline">
                          Apply
                        </Button>
                      ) : (
                        <Button type="button" onClick={() => { setCouponApplied(null); setCouponCode(''); }} variant="outline">
                          Remove
                        </Button>
                      )}
                    </div>
                    {couponApplied && (
                      <p className="text-sm text-green-600 flex items-center gap-1">
                        <Check className="w-4 h-4" />
                        Coupon applied! You saved ₹{couponApplied.type === 'flat' ? couponApplied.discount : Math.round(299 * couponApplied.discount / 100)}
                      </p>
                    )}
                    <p className="text-xs text-slate-500">Demo codes: SAVE50, NEW100, STUDENT20</p>
                  </div>

                  <div className="mt-6 pt-6 border-t">
                    <Button
                      onClick={handlePayment}
                      disabled={processing || !paymentMethod}
                      className="w-full h-12 text-base bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600"
                    >
                      {processing ? 'Processing...' : `Pay ₹${calculateFinalPrice()} with ${paymentOptions.find(o => o.id === paymentMethod)?.name || 'selected method'}`}
                    </Button>
                  </div>

                  <div className="mt-4 flex items-center justify-center gap-2 text-sm text-slate-600">
                    <Shield className="w-4 h-4" />
                    <span>Secure payment powered by industry-standard encryption</span>
                  </div>
                  </>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Order Summary */}
            <div className="md:col-span-1">
              <Card className="shadow-lg border-slate-200 sticky top-4">
                <CardHeader>
                  <CardTitle>Order Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-600 mb-2">Your Details:</p>
                    <div className="space-y-1 text-sm">
                      <p><span className="font-medium">Name:</span> {formData.name}</p>
                      <p><span className="font-medium">Rank:</span> {formData.rank}</p>
                      <p><span className="font-medium">Exam:</span> {formData.examType}</p>
                      <p><span className="font-medium">Category:</span> {formData.category}</p>
                    </div>
                  </div>

                  <div className="pt-4 border-t space-y-2">
                    <p className="text-sm text-slate-600">What you'll get:</p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">Personalized college predictions</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">Historical cutoff analysis</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">Detailed PDF report</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">Email delivery</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">Referral bonus eligibility</span>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-slate-600">Subtotal:</span>
                      <span className="text-sm">₹299</span>
                    </div>
                    {couponApplied && (
                      <div className="flex justify-between items-center mb-2 text-green-600">
                        <span className="text-sm">Discount ({couponCode}):</span>
                        <span className="text-sm">-₹{couponApplied.type === 'flat' ? couponApplied.discount : Math.round(299 * couponApplied.discount / 100)}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center text-lg font-bold">
                      <span>Total:</span>
                      <span>₹{calculateFinalPrice()}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payment;